import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import type { Order } from "../../lib/types.ts";
import type { BoardStateSelection } from "../../state/adresse.ts";
import { hubZeileLaden } from "../../api/bestellungen.ts";
import { NutzerFehler } from "../../lib/fehlertext.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";
import {
  bestellgruppeAnlegen, bestellgruppeAufloesen, bestellgruppeLaden,
  bestellgruppenMitgliedHinzufuegen, bestellgruppenMitgliedLoesen, bestellgruppenSendungSetzen,
  type Bestellgruppe,
} from "../../api/bestellgruppen.ts";

/**
 * Gruppen laden und vor jeder Mutation die betroffenen Serverversionen erheben. Jede Meldung dieses Hooks ist
 * ein Fehler und geht in den Fehlerstapel (Fehleranzeige Paket 1); einen Toast gibt es hier nicht.
 */
export function useBestellgruppen(
  drawerId: string | null,
  bestellungen: readonly Order[],
  boardStates: BoardStateSelection,
  bestandVollNeuLaden: (states?: BoardStateSelection) => void,
  setAuswahlLeerenSignal: Dispatch<SetStateAction<number>>,
) {
  const [drawerGruppe, setDrawerGruppe] = useState<Bestellgruppe | null>(null);
  const [gruppenAnsicht, setGruppenAnsicht] = useState<Bestellgruppe | null>(null);
  const gruppenIdDerOffenenBestellung = drawerId !== null
    ? (bestellungen.find((eintrag) => String(eintrag.id) === drawerId)?.orderGroupId as string | null | undefined) ?? null
    : null;

  useEffect(() => {
    if (!gruppenIdDerOffenenBestellung) {
      setDrawerGruppe(null);
      return;
    }
    let abgebrochen = false;
    bestellgruppeLaden(gruppenIdDerOffenenBestellung)
      .then((gruppe) => {
        if (!abgebrochen) setDrawerGruppe(gruppe);
      })
      .catch((fehler: unknown) => {
        // L6: ein Drawer, der inzwischen gewechselt oder zu ist, hat keine Flaeche mehr, der Fehler wird verworfen.
        if (!abgebrochen) {
          console.error("Bestellgruppe konnte nicht geladen werden:", fehler);
          setDrawerGruppe(null);
          // L2: der Sendungsabschnitt hat keine eigene Flaeche fuer den Ladefehler - ohne die Gruppe zeigt er die
          // Bestellung einzeln, und das sieht aus wie "gehoert zu keiner Gruppe". Der Satz nennt die Bestellung nicht,
          // damit gleiche Fehlschlaege verschiedener Drawer im Stapel zusammenlaufen (L5).
          meldeFehler(
            fehler,
            "Die Bestellgruppe konnte nicht geladen werden – die Sendung zeigt die Bestellung einzeln.",
          );
        }
      });
    return () => { abgebrochen = true; };
  }, [gruppenIdDerOffenenBestellung]);

  /**
   * Die frischen `rowVersion`s der betroffenen Bestellungen. Aus der HUB-Zeile (`hubZeileLaden`), nicht aus
   * `bestellungLaden`: die Mutation braucht nur die Hub-Version, und `bestellungLaden` lehnt seit Fehleranzeige Paket 2
   * (Task 7, E2) bei einem JTL-Ausfall ab - jede Gruppenmutation haengte dann an einer Anreicherung, die sie gar nicht
   * nutzt (Fix-Runde 1, G2). Die Zeilen gehen nur in die Versionen ein, nie in den Store: eine Hub-Zeile ohne JTL-Felder
   * wuerde dort die volle ersetzen. `null` (404) und eine Zeile ohne `rowVersion` (kein Overlay) bleiben wie bisher
   * eine verweigerte Mutation.
   */
  function frischeBestellversionen(orderIds: readonly string[]): Promise<Record<string, number>> {
    return Promise.all(orderIds.map((id) => hubZeileLaden(id))).then((bestellungen) => {
      const versionen: Record<string, number> = {};
      for (const bestellung of bestellungen) {
        if (!bestellung || !Number.isInteger(bestellung.rowVersion)) {
          throw new NutzerFehler("Eine betroffene Bestellung konnte nicht mit aktueller Version geladen werden.");
        }
        versionen[String(bestellung.id)] = bestellung.rowVersion as number;
      }
      return versionen;
    });
  }

  /**
   * Liefert `true`, wenn die Mutation durchging, und `false`, wenn sie scheiterte (R6, Fehleranzeige Paket 1):
   * vorher loeste ein Fehlschlag als Erfolg (`Promise<void>`) auf, und `gruppenSendungSetzen` konnte ihn dem
   * Aufrufer nicht zeigen. Bewusst KEINE Ablehnung: die Aufrufer sind fire-and-forget (`hinzufuegen`, `loesen`,
   * `aufloesen`) bzw. ein Klick-Handler ohne eigenen Fangzweig; eine Ablehnung ginge dort als unbehandeltes
   * Promise an den globalen Faenger und gaebe dieselbe Meldung ein zweites Mal. Der Fehler selbst steht
   * bereits im Fehlerstapel.
   */
  function gruppenMutation(
    groupId: string,
    betroffeneOrderIds: (gruppe: Bestellgruppe) => readonly string[],
    mutation: (gruppe: Bestellgruppe, versionen: Record<string, number>) => Promise<Bestellgruppe>,
  ): Promise<boolean> {
    return bestellgruppeLaden(groupId)
      .then((frischeGruppe) => frischeBestellversionen(betroffeneOrderIds(frischeGruppe)).then((versionen) => mutation(frischeGruppe, versionen)))
      .then((gruppe) => {
        setDrawerGruppe(gruppe);
        setGruppenAnsicht((ansicht) => (ansicht?.id === gruppe.id ? gruppe : ansicht));
        bestandVollNeuLaden(boardStates);
        return true;
      })
      .catch((fehler: unknown) => {
        console.error("Bestellgruppe konnte nicht geändert werden:", fehler);
        meldeFehler(fehler, "Die Bestellgruppe konnte nicht geändert werden.");
        return false;
      });
  }

  function gruppenMitgliedHinzufuegen(orderId: string): void {
    const gruppe = gruppenAnsicht ?? drawerGruppe;
    if (!gruppe) return;
    gruppenMutation(
      gruppe.id,
      () => [orderId],
      (gruppe, versionen) => bestellgruppenMitgliedHinzufuegen(gruppe.id, orderId, gruppe.rowVersion, versionen),
    );
  }

  function gruppenMitgliedLoesen(orderId: string): void {
    const gruppe = gruppenAnsicht ?? drawerGruppe;
    if (!gruppe) return;
    gruppenMutation(
      gruppe.id,
      (gruppe) => {
        const aktive = gruppe.members.filter((mitglied) => mitglied.membershipState === "active").map((mitglied) => mitglied.orderId);
        return aktive.length === 2 ? aktive : [orderId];
      },
      (gruppe, versionen) => bestellgruppenMitgliedLoesen(gruppe.id, orderId, gruppe.rowVersion, versionen),
    );
  }

  function gruppeAufloesen(retainedOrderIds: string[]): void {
    const gruppe = gruppenAnsicht ?? drawerGruppe;
    if (!gruppe) return;
    gruppenMutation(
      gruppe.id,
      (gruppe) => gruppe.members.filter((mitglied) => mitglied.membershipState === "active").map((mitglied) => mitglied.orderId),
      (gruppe, versionen) => bestellgruppeAufloesen(gruppe.id, retainedOrderIds, gruppe.rowVersion, versionen),
    );
  }

  function gruppenSendungSetzen(shipmentId: string): Promise<boolean> {
    const gruppe = gruppenAnsicht ?? drawerGruppe;
    if (!gruppe) return Promise.resolve(false);
    return gruppenMutation(
      gruppe.id,
      (gruppe) => gruppe.members.filter((mitglied) => mitglied.membershipState === "active").map((mitglied) => mitglied.orderId),
      (gruppe, versionen) => bestellgruppenSendungSetzen(gruppe.id, shipmentId, gruppe.rowVersion, versionen),
    );
  }

  function bestellgruppeErstellen(orderIds: string[]): void {
    const expectedOrderVersions: Record<string, number> = {};
    for (const id of orderIds) {
      const bestellung = bestellungen.find((eintrag) => eintrag.id === id);
      if (!bestellung || !Number.isInteger(bestellung.rowVersion)) {
        meldeFehler("Die Bestellgruppe konnte nicht angelegt werden.");
        return;
      }
      expectedOrderVersions[id] = bestellung.rowVersion as number;
    }
    bestellgruppeAnlegen(orderIds, expectedOrderVersions)
      .then(() => {
        setAuswahlLeerenSignal((wert) => wert + 1);
        bestandVollNeuLaden(boardStates);
      })
      .catch((fehler: unknown) => {
        console.error("Bestellgruppe konnte nicht angelegt werden:", fehler);
        meldeFehler(fehler, "Die Bestellgruppe konnte nicht angelegt werden.");
      });
  }

  return {
    drawer: drawerGruppe,
    ansicht: gruppenAnsicht,
    setAnsicht: setGruppenAnsicht,
    gruppenId: gruppenIdDerOffenenBestellung,
    hinzufuegen: gruppenMitgliedHinzufuegen,
    loesen: gruppenMitgliedLoesen,
    aufloesen: gruppeAufloesen,
    sendungSetzen: gruppenSendungSetzen,
    erstellen: bestellgruppeErstellen,
  };
}
